import React, { useEffect, useRef, useState } from 'react';
import styles from '../Styles/EventOps.module.css';
import { worstCaseSms, audienceSmsCheck } from '../campaignLength';
import { CHANNELS, CHANNEL_LABEL, PLACEHOLDERS, smsWarnings } from './placeholders';

const SHARED_PLACEHOLDER = 'Hi {parent}, {count} photos of {athlete} from {event} are ready. {app}';
const CHANNEL_PLACEHOLDER = {
  push: 'Hi {parent}, {count} photos of {athlete} from {event} are ready.',
  sms: 'Hi {parent}, photos of {athlete} are ready: {app}',
  email: 'Hi {parent}, {count} photos of {athlete} from {event} are ready.',
};

/**
 * SCRUM-449: the one message editor — the audience builder, the Campaigns
 * page and the per-occurrence Edit all render this.
 *
 * Controlled: `bodies` is `{ push?, sms?, email? }`; every edit calls
 * `onChange({ title, subject, bodies })`. With "Customize per channel" off
 * there is one box and its text goes to every selected channel; on, one box
 * per channel. Pills insert at the caret of whichever box was last focused.
 *
 * `lengthContext` = `{ parents, eventName, app, deadline, lead }` sizes the
 * SMS counter to the longest message this audience will actually receive,
 * including the contact line of a first-ever contact — the combined
 * MUTE/STOP line, or (SCRUM-449, `lead: true`) the plain STOP line for a
 * lead-only audience, which names no event to mute.
 *
 * Every SMS must fit in ONE text in that worst case: when it does not, a red
 * error shows and `onValidityChange(true)` tells the parent to block saving
 * or sending (and `false` once it fits again).
 *
 * `audienceAware` (SCRUM-449, AudienceBuilder only): `lengthContext.parents`
 * is a REAL audience, not a stand-in for one, so the check follows the API's
 * actual send semantics instead of one audience-wide worst case — per
 * parent, {athlete} tries their real first name(s) first and only falls back
 * to "your athlete" if that alone runs over one segment. INVALID only when a
 * parent is still over after that fallback; a parent saved BY the fallback
 * shows as an amber, non-blocking note instead ("N parents' texts will use
 * 'your athlete' to fit"). Campaigns/CampaignTimeline never set this — they
 * have no real audience, so they keep the single worst-case check.
 *
 * SCRUM-449: push never needs `{app}` — tapping the notification opens the
 * app, so the `{app}` pill is withheld from a box that serves push only
 * (the per-channel push box, or the shared box when push is the only
 * selected channel). SMS and email keep the pill everywhere. If the push
 * body ends up containing `{app}` anyway (e.g. typed before switching
 * channels), a small informational note shows under it — it never blocks.
 */
export default function MessageEditor({
  channels = [],
  title = '',
  subject = '',
  bodies = {},
  onChange,
  lengthContext = {},
  onValidityChange,
  audienceAware = false,
}) {
  const [perChannel, setPerChannel] = useState(
    () => channels.length > 1 && new Set(channels.map((ch) => bodies[ch] ?? '')).size > 1
  );
  const [activeBox, setActiveBox] = useState('shared');
  const sharedRef = useRef(null);
  const channelRefs = useRef({});

  const split = perChannel && channels.length > 1;
  const targets = channels.length ? channels : CHANNELS;
  const sharedText = targets.map((ch) => bodies[ch]).find((b) => b) ?? '';

  const emit = (patch) => onChange?.({ title, subject, bodies, ...patch });
  const writeShared = (value) => {
    const next = { ...bodies };
    for (const ch of targets) next[ch] = value;
    emit({ bodies: next });
  };
  const writeChannel = (ch, value) => emit({ bodies: { ...bodies, [ch]: value } });

  const togglePerChannel = (on) => {
    setPerChannel(on);
    // Both directions start from the shared text: on → every box prefilled
    // with it; off → every channel collapses back to it.
    const next = { ...bodies };
    for (const ch of channels) if (!on || !String(next[ch] ?? '').trim()) next[ch] = sharedText;
    emit({ bodies: next });
    setActiveBox(on ? (channels[0] ?? 'shared') : 'shared');
  };

  // The channel(s) the currently focused box actually writes to — 'shared'
  // writes to every selected channel, a per-channel box writes to just its own.
  const insertTarget = split && channels.includes(activeBox) ? activeBox : 'shared';
  const insertTargetChannels = insertTarget === 'shared' ? targets : [insertTarget];
  // SCRUM-449: push never needs {app} — withhold the pill from a box that
  // serves push and nothing else.
  const pillPlaceholders =
    insertTargetChannels.length === 1 && insertTargetChannels[0] === 'push'
      ? PLACEHOLDERS.filter((p) => p !== 'app')
      : PLACEHOLDERS;

  const insertPlaceholder = (token) => {
    const placeholder = `{${token}}`;
    const target = insertTarget;
    const el = target === 'shared' ? sharedRef.current : channelRefs.current[target];
    const current = target === 'shared' ? sharedText : String(bodies[target] ?? '');
    const write = (value) => (target === 'shared' ? writeShared(value) : writeChannel(target, value));
    if (!el) {
      write(current + placeholder);
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    write(current.slice(0, start) + placeholder + current.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + placeholder.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const hasPush = channels.includes('push');
  // SCRUM-449: tapping the notification opens the app — {app} is never
  // needed on push, but a note (not a block) if it ends up there anyway.
  const pushText = hasPush ? (split ? bodies.push : sharedText) : '';
  const pushHasAppPill = /\{app\}/.test(String(pushText ?? ''));
  const hasSms = channels.includes('sms');
  const smsText = split && String(bodies.sms ?? '').trim() ? bodies.sms : sharedText;
  // SCRUM-434: count what is SENT, not what is typed — placeholders expand
  // and the contact line is appended on a first contact.
  const smsCtx = {
    parents: lengthContext.parents ?? [],
    eventName: lengthContext.eventName ?? '',
    app: lengthContext.app,
    deadline: lengthContext.deadline,
    lead: lengthContext.lead,
  };
  // SCRUM-449: AudienceBuilder checks the API's real per-parent send
  // semantics (audienceSmsCheck); everyone else uses the single
  // audience-wide worst case (worstCaseSms) — see the audienceAware doc above.
  const audienceCheck = audienceAware ? audienceSmsCheck(smsText, smsCtx) : null;
  const smsWorst = audienceAware ? audienceCheck.worst : worstCaseSms(smsText, smsCtx);
  const fallbackCount = audienceAware ? audienceCheck.fallbackCount : 0;
  const warnings = hasSms ? smsWarnings(smsText) : [];
  // SCRUM-449: every SMS is one text, worst case — anything longer is blocked.
  const invalid = hasSms && (audienceAware ? audienceCheck.invalid : smsWorst.segments > 1);
  const validityRef = useRef(onValidityChange);
  validityRef.current = onValidityChange;
  useEffect(() => {
    validityRef.current?.(invalid);
  }, [invalid]);

  return (
    <div className={styles.audienceTemplate}>
      {channels.includes('push') && (
        <label>
          Title
          <input value={title} onChange={(e) => emit({ title: e.target.value })} placeholder="ARND" />
        </label>
      )}
      {channels.includes('email') && (
        <label>
          Subject
          <input value={subject} onChange={(e) => emit({ subject: e.target.value })} placeholder="ARND test" />
        </label>
      )}
      <div className={styles.placeholderChips}>
        {pillPlaceholders.map((p) => (
          <button key={p} type="button" onClick={() => insertPlaceholder(p)}>{`{${p}}`}</button>
        ))}
        <span
          className={styles.subtle}
          title="{count} reads 'the' and {athlete} reads 'your athlete' for a parent with no matched photos"
        >
          no matches → {'{count}'} = "the"
        </span>
      </div>
      {channels.length > 1 && (
        <label className={styles.channelOption}>
          <input type="checkbox" checked={perChannel} onChange={(e) => togglePerChannel(e.target.checked)} />
          Customize per channel
        </label>
      )}
      {split ? (
        <div className={styles.channelBodies}>
          {channels.map((ch) => (
            <div key={ch} className={`${styles.channelBody} ${activeBox === ch ? styles.channelBodyActive : ''}`}>
              <span className={styles.channelGroupLabel}>{CHANNEL_LABEL[ch]}</span>
              <textarea
                ref={(el) => { channelRefs.current[ch] = el; }}
                rows={4}
                value={bodies[ch] ?? ''}
                aria-label={`${CHANNEL_LABEL[ch]} message`}
                onFocus={() => setActiveBox(ch)}
                onChange={(e) => writeChannel(ch, e.target.value)}
                placeholder={CHANNEL_PLACEHOLDER[ch]}
              />
            </div>
          ))}
        </div>
      ) : (
        <textarea
          ref={sharedRef}
          rows={4}
          value={sharedText}
          onFocus={() => setActiveBox('shared')}
          onChange={(e) => writeShared(e.target.value)}
          placeholder={SHARED_PLACEHOLDER}
        />
      )}
      {pushHasAppPill && (
        <div className={styles.subtle} data-testid="push-app-note">
          Push opens the app on tap — no link needed.
        </div>
      )}
      {hasSms && (
        <div
          className={styles.subtle}
          title="Longest message this audience receives, with the MUTE/STOP line of a first-ever text added"
        >
          SMS length: {smsWorst.length} chars · {smsWorst.segments} segment{smsWorst.segments === 1 ? '' : 's'}
        </div>
      )}
      {invalid && (
        <div className={styles.smsError} role="alert" data-testid="sms-too-long">
          SMS must fit in one text ({smsWorst.limit} characters) — {smsWorst.over} over
        </div>
      )}
      {!invalid && fallbackCount > 0 && (
        <div className={styles.smsWarn} role="status" data-testid="sms-athlete-fallback">
          {fallbackCount} parent{fallbackCount === 1 ? "'s text" : "s' texts"} will use 'your athlete' to fit
        </div>
      )}
      {warnings.length > 0 && (
        <div className={styles.smsWarn} role="alert">
          SMS wording carriers may filter as promotional: {warnings.map((w) => `"${w}"`).join(', ')}. It still sends.
        </div>
      )}
    </div>
  );
}
